import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { registerUnauthorizedHandler } from "../api/fleetApi";
import { userApi, ApiError, type UserProfile } from "../api/userApi";
import { identify, resetIdentity, trackDailyActive } from "../lib/analytics";
import { setSentryUser } from "../lib/sentryInit";
import {
  type Account,
  listAccounts,
  getActiveAccount,
  addAccount,
  switchAccount as switchAccountStore,
  removeAccount,
  clearAllAccounts,
} from "../lib/accountStore";

export type LogoutReason = "expired" | null;
export type ProvisioningState = null | "provisioning" | "timed_out";

const POLL_INTERVAL_MS  = 5_000;
const POLL_MAX_ATTEMPTS = 12; // 60 s total

interface AuthState {
  token:             string | null;
  logoutReason:      LogoutReason;
  profile:           UserProfile | null;
  profileLoading:    boolean;
  provisioningState: ProvisioningState;
  login:             (token: string, refreshToken: string) => void;
  logout:            (reason?: LogoutReason) => void;
  // Multi-account (#1649)
  accounts:          Account[];
  activeSub:         string | null;
  switchAccount:     (sub: string) => void;
  signOutAccount:    (sub: string) => void;
}

const AuthContext = createContext<AuthState | null>(null);

function parseTokenExpiry(jwt: string | null): number | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // accounts + active drive everything; the active account's tokens are mirrored
  // into localStorage.jwt by accountStore so the API modules keep working.
  const [accounts,          setAccounts]          = useState<Account[]>(() => listAccounts());
  const [activeSub,         setActiveSub]          = useState<string | null>(() => getActiveAccount()?.sub ?? null);
  const [token,             setToken]             = useState<string | null>(() => getActiveAccount()?.jwt ?? null);
  const [logoutReason,      setLogoutReason]      = useState<LogoutReason>(null);
  const [profile,           setProfile]           = useState<UserProfile | null>(null);
  const [profileLoading,    setProfileLoading]    = useState<boolean>(!!getActiveAccount());
  const [provisioningState, setProvisioningState] = useState<ProvisioningState>(null);

  // Re-sync React state from the account store (after add/switch/remove).
  const syncFromStore = useCallback(() => {
    const list   = listAccounts();
    const active  = getActiveAccount();
    setAccounts(list);
    setActiveSub(active?.sub ?? null);
    setToken(active?.jwt ?? null);
  }, []);

  // Remove ONE account; if none remain, this is a full logout.
  const signOutAccount = useCallback((sub: string) => {
    const next = removeAccount(sub);
    setLogoutReason(null);
    if (!next) {
      setProfile(null);
      setProfileLoading(false);
      setProvisioningState(null);
    } else {
      // switching to a different active account → fresh profile load
      setProfile(null);
      setProvisioningState(null);
      setProfileLoading(true);
    }
    syncFromStore();
  }, [syncFromStore]);

  // Full logout: clear ALL accounts (used by 401/expiry on last account).
  const logout = useCallback((reason: LogoutReason = null) => {
    clearAllAccounts();
    resetIdentity();                    // #umami — clear Umami distinct_id
    setSentryUser(null);                // #3095 — clear Sentry user scope
    setLogoutReason(reason);
    setProfile(null);
    setProfileLoading(false);
    setProvisioningState(null);
    syncFromStore();
  }, [syncFromStore]);

  // Switch the active workspace (token swaps → profile/hives/etc reload).
  const switchAccount = useCallback((sub: string) => {
    const acct = switchAccountStore(sub);
    if (!acct) return;
    setLogoutReason(null);
    setProfile(null);
    setProvisioningState(null);
    setProfileLoading(true);
    syncFromStore();
  }, [syncFromStore]);

  const login = useCallback((t: string, refreshToken: string) => {
    addAccount(t, refreshToken); // adds-or-updates + makes active + mirrors
    setLogoutReason(null);
    setProfile(null);
    setProvisioningState(null);
    setProfileLoading(true);
    syncFromStore();
  }, [syncFromStore]);

  // Fetch profile on login / page reload; poll on 404 (provisioning) or 5xx (server error)
  useEffect(() => {
    if (!token) {
      setProfile(null);
      setProfileLoading(false);
      setProvisioningState(null);
      return;
    }

    let cancelled   = false;
    let retryCount  = 0;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    async function fetchProfile() {
      try {
        const p = await userApi.getMe();
        if (!cancelled) {
          // #2446 — identify with cognito_sub as person_id + track daily active
          if (p.sub) identify(p.sub, { email: p.email, tier: p.tier });
          // Card #3095 — tag Sentry scope with cognito sub only (no email/name).
          // beforeSend additionally scrubs anything else that slips in.
          setSentryUser(p.sub ?? null);
          trackDailyActive();
          setProfile(p);
          setProfileLoading(false);
          setProvisioningState(null);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : 0;

        if (status === 401) {
          // callUnauthorizedHandler already fired inside adminFetch → logout incoming
          return;
        }

        if (status === 404 || status >= 500) {
          retryCount += 1;
          if (retryCount >= POLL_MAX_ATTEMPTS) {
            setProfileLoading(false);
            setProvisioningState("timed_out");
            return;
          }
          // 404 = user not provisioned yet → show provisioning banner
          // 5xx = transient server error → keep loading spinner (profileLoading stays true)
          if (status === 404) setProfileLoading(false);
          setProvisioningState(status === 404 ? "provisioning" : null);
          pollTimer = setTimeout(fetchProfile, POLL_INTERVAL_MS);
        } else {
          // Unknown / network error — clear loading, leave profile null
          setProfile(null);
          setProfileLoading(false);
        }
      }
    }

    setProfileLoading(true);
    fetchProfile();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [token]);

  // Register with API client so a 401 on the ACTIVE account signs out just that
  // account (other workspaces stay signed in). If it was the last account this
  // is a full logout. (#1649)
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      const active = getActiveAccount();
      if (active) {
        signOutAccount(active.sub);
        setLogoutReason("expired");
      } else {
        logout("expired");
      }
    });
  }, [logout, signOutAccount]);

  // Proactive expiry timer — when the ACTIVE token expires, sign out just that
  // account (not the others).
  useEffect(() => {
    if (!token) return;
    const exp = parseTokenExpiry(token);
    if (!exp) return;
    const now = Date.now();
    const expire = () => {
      const active = getActiveAccount();
      if (active) { signOutAccount(active.sub); setLogoutReason("expired"); }
      else logout("expired");
    };
    if (exp <= now) { expire(); return; }
    const delay = Math.max(0, exp - now);
    const timer = setTimeout(expire, delay);
    return () => clearTimeout(timer);
  }, [token, logout, signOutAccount]);

  return (
    <AuthContext.Provider value={{
      token, logoutReason, profile, profileLoading, provisioningState, login, logout,
      accounts, activeSub, switchAccount, signOutAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
